import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-flexbox-grid';
import { Field } from 'redux-form';
import { injectIntl } from 'react-intl';

import { Input, PhoneInput, Select } from 'shared/components/reduxFormComponents';
import { Checkbox, HintQuestion } from 'shared/components';
import DateOfBirth from 'modules/user/components/_forms/UserDetailsForm/components/DateOfBirth';
import requestStatuses from 'modules/common/constants/requestsStatuses';
import { appointmentFormFieldLabels } from 'modules/appointmentsManagement/constants/formFieldLabels';
import { apiErrorMessagesMap } from 'modules/user/constants/apiErrorCodes';
import { ASTERISK } from 'shared/components/_constants/label';
import { RestrictedUserField } from '../index';

import theme from '../../CreateAppointmentForm.scss';

const HintQuestionWithIntl = injectIntl(HintQuestion);

const userReadPermissions = {
  canSeeUserAccount: false,
  canSeePolicyId: false,
  canSeeName: false,
  canSeeGender: false,
  canSeeBirthDate: false,
  canSeeEmail: false,
  canSeePhone: false,
  canSeeAddress: false,
  canSeeDescription: false,
  canSeeAvatar: false,
};

const userUpdatePermissions = {
  canUpdateUserAccount: false,
  canUpdatePolicyId: false,
  canUpdateName: false,
  canUpdateGender: false,
  canUpdateBirthDate: false,
  canUpdateEmail: false,
  canUpdatePhone: false,
  canUpdateAdditionalPhone: false,
  canUpdateAddress: false,
  canUpdateDescription: false,
  canUpdateAvatar: false,
};

const propTypes = {
  type: PropTypes.oneOf(['patient', 'customer']).isRequired, // eslint-disable-line
  patientIndex: PropTypes.number.isRequired,
  isCustomer: PropTypes.bool.isRequired,
  customerIsPatient: PropTypes.bool.isRequired,
  userCheckStatus: PropTypes.string.isRequired,
  patientIdPreValidation: PropTypes.func.isRequired,
  handleUserIdChange: PropTypes.func.isRequired,
  untouch: PropTypes.func.isRequired,
  userGenderOptions: PropTypes.array.isRequired,
  disableCustomerIsPatient: PropTypes.bool.isRequired,
  setCustomerIsPatient: PropTypes.func.isRequired,
  userFieldConfig: PropTypes.object,
  viewportRef: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  userPermissions: PropTypes.object,
};

const defaultProps = {
  userFieldConfig: {
    requiredFields: [],
  },
  userPermissions: { read: userReadPermissions, update: userUpdatePermissions },
};

const UserDetailsForm = ({
  customerIsPatient,
  isCustomer,
  patientIndex,
  userCheckStatus,
  patientIdPreValidation,
  handleUserIdChange,
  untouch,
  userGenderOptions,
  viewportRef,
  disableCustomerIsPatient,
  setCustomerIsPatient,
  userFieldConfig,
  type,
  isMobile,
  userPermissions,
}) => {
  return (
    <div>
      <Row className={ theme.fieldset }>
        <If condition={ !isCustomer }>
          <Col xs={ 12 }>
            <div className={ theme.inlineBlock }>
              <Checkbox
                id="customerIsPatient"
                testId="customerIsPatient-checkbox"
                name="customerIsPatient"
                onClick={ ({ target: { checked } }) => setCustomerIsPatient(checked ? patientIndex : null) }
                checked={ customerIsPatient }
                label={{
                  id: 'Patient.Appointment.Create.CustomerIsPatient.Checkbox',
                  defaultMessage: '** I’m the patient',
                }}
                labelSuffix={ !disableCustomerIsPatient && !isMobile && (
                  <HintQuestionWithIntl
                    className={ theme.iconQuestion }
                    tooltipMessage={{
                      id: 'AdminConsole.Appointments.Create.Dialog.CustomerIsPatient.Checkbox.Tooltip',
                      defaultMessage: '** Removing the checkbox will clear the patient details',
                    }}
                    iconName="exclamation-outline"
                  />)
                }
                disabled
                rootClassName={ theme.isPatient }
              />
            </div>
          </Col>
        </If>
        <Col xs={ 12 } md={ 6 }>
          <Field
            name={ customerIsPatient ? 'customer.externalId' : `patient[${ patientIndex }].externalId` }
            testId="patient-externalId"
            component={ !userPermissions.read.canSeePolicyId ? RestrictedUserField : Input }
            label={ userFieldConfig.externalIdLabel }
            labelSuffix={ userFieldConfig.requiredFields.includes('externalId') ? ASTERISK : undefined }
            placeholder={ userFieldConfig.externalIdPlaceholder }
            onBlur={ ({ target: { value } }) => {
              handleUserIdChange('patient', value);
              untouch(customerIsPatient ? 'customer.externalId' : `patient[${ patientIndex }].externalId`);
            } }
            status={ userCheckStatus }
            greenSuccessStatus
            loading={ userCheckStatus === requestStatuses.pending }
            errorMessagesMap={ apiErrorMessagesMap }
            maxLength={ 15 }
            disabled={ (customerIsPatient && !isCustomer) || !userPermissions.update.canUpdatePolicyId }
            validate={ patientIdPreValidation }
            inputMode="numeric"
          />
        </Col>
        <Col xs={ 12 } md={ 6 }>
          <Field
            name={ customerIsPatient ? 'customer.firstName' : `patient[${ patientIndex }].firstName` }
            testId="patient-firstName"
            component={ !userPermissions.read.canSeeName ? RestrictedUserField : Input }
            label={ appointmentFormFieldLabels.firstName }
            labelSuffix={ userFieldConfig.requiredFields.includes('firstName') ? ASTERISK : undefined }
            placeholder={{
              id: 'Patient.Appointment.Create.GivenName.Placeholder',
              defaultMessage: '** Type user’s first name',
            }}
            onBlur={ () => { untouch(isCustomer ? 'customer.firstName' : `patient[${ patientIndex }].firstName`); } }
            errorMessagesMap={ apiErrorMessagesMap }
            maxLength={ 200 }
            disabled={ (customerIsPatient && !isCustomer) || !userPermissions.update.canUpdateName }
          />
        </Col>
        <Col xs={ 12 } md={ 6 }>
          <Field
            name={ customerIsPatient ? 'customer.lastName' : `patient[${ patientIndex }].lastName` }
            testId="patient-lastName"
            component={ !userPermissions.read.canSeeName ? RestrictedUserField : Input }
            label={ appointmentFormFieldLabels.lastName }
            labelSuffix={ userFieldConfig.requiredFields.includes('lastName') ? ASTERISK : undefined }
            placeholder={{
              id: 'Patient.Appointment.Create.LastName.Placeholder',
              defaultMessage: '** Type user’s last name',
            }}
            onBlur={ () => { untouch(customerIsPatient ? 'customer.lastName' : `patient[${ patientIndex }].lastName`); } }
            errorMessagesMap={ apiErrorMessagesMap }
            maxLength={ 200 }
            disabled={ (customerIsPatient && !isCustomer) || !userPermissions.update.canUpdateName }
          />
        </Col>
        <Col xs={ 12 } md={ 6 }>
          <Field
            name={ customerIsPatient ? 'customer.gender' : `patient[${ patientIndex }].gender` }
            testId="patient-gender"
            component={ !userPermissions.read.canSeeGender ? RestrictedUserField : Select }
            items={ userGenderOptions }
            label={ appointmentFormFieldLabels.gender }
            labelSuffix={ userFieldConfig.requiredFields.includes('gender') ? ASTERISK : undefined }
            placeholder={{
              id: 'Patient.Appointment.Create.Gender.Placeholder',
              defaultMessage: '** Select user’s gender',
            }}
            clearable={ false }
            onBlur={ () => { untouch(customerIsPatient ? 'customer.gender' : `patient[${ patientIndex }].gender`); } }
            errorMessagesMap={ apiErrorMessagesMap }
            viewportRef={ viewportRef }
            disabled={ (customerIsPatient && !isCustomer) || !userPermissions.update.canUpdateGender }
          />
        </Col>
        <Col xs={ 12 } md={ 6 }>
          <Field
            name={ customerIsPatient ? 'customer.dateOfBirth' : `patient[${ patientIndex }].dateOfBirth` }
            testId="patient-dateOfBirth"
            component={ !userPermissions.read.canSeeBirthDate ? RestrictedUserField : DateOfBirth }
            label={ appointmentFormFieldLabels.dateOfBirth }
            labelSuffix={ userFieldConfig.requiredFields.includes('dateOfBirth') ? ASTERISK : undefined }
            placeholder={{
              id: 'Patient.Appointment.Create.YearOfBirth.Placeholder',
              defaultMessage: '** yyyy',
            }}
            onBlur={ () => { untouch(customerIsPatient ? 'customer.dateOfBirth' : `patient[${ patientIndex }].dateOfBirth`); } }
            errorMessagesMap={ apiErrorMessagesMap }
            disabled={ (customerIsPatient && !isCustomer) || !userPermissions.update.canUpdateBirthDate }
            dateFormat={ userFieldConfig.birthDateFormat }
          />
        </Col>
        <Col xs={ 12 } md={ 6 }>
          <Field
            name={ customerIsPatient
              ? `customer.email${ !userPermissions.read.canSeeEmail ? '-mask' : '' }`
              : `patient[${ patientIndex }].email${ !userPermissions.read.canSeeEmail ? '-mask' : '' }`
            }
            testId={ `patient-email${ !userPermissions.read.canSeeEmail ? '-mask' : '' }` }
            component={ !userPermissions.read.canSeeEmail ? RestrictedUserField : Input }
            labelSuffix={ userFieldConfig.requiredFields.includes('email') ? ASTERISK : undefined }
            label={ appointmentFormFieldLabels.email }
            placeholder={{
              id: 'Patient.Appointment.Create.Email.Placeholder',
              defaultMessage: '** Type user’s email',
            }}
            errorMessagesMap={ apiErrorMessagesMap }
            onBlur={ () => { untouch(customerIsPatient ? 'customer.email' : `patient[${ patientIndex }].email`); } }
            disabled={ (customerIsPatient && !isCustomer) || !userPermissions.update.canUpdateEmail }
            inputMode="email"
          />
        </Col>
        <Col xs={ 12 } md={ 6 }>
          <Field
            name={ customerIsPatient
              ? `customer.phoneNumber${ !userPermissions.read.canSeePhone ? '-mask' : '' }`
              : `patient[${ patientIndex }].phoneNumber${ !userPermissions.read.canSeePhone ? '-mask' : '' }`
            }
            testId={ `${ type }-phoneNumber${ userPermissions.read.canSeePhone ? '-mask' : '' }` }
            component={ !userPermissions.read.canSeePhone ? RestrictedUserField : PhoneInput }
            label={ appointmentFormFieldLabels.phoneNumber }
            labelSuffix={ userFieldConfig.requiredFields.includes('phoneNumber') ? ASTERISK : undefined }
            phonePlaceholder={{
              id: 'AdminConsole.Appointments.Create.Dialog.PhoneNo.Placeholder',
              defaultMessage: '** Type user’s phone',
            }}
            codePlaceholder={{
              id: 'AdminConsole.Appointments.Create.Dialog.PhoneNo.Code.Placeholder',
              defaultMessage: '** Type code',
            }}
            errorMessagesMap={ apiErrorMessagesMap }
            onBlur={ () => { untouch(isCustomer ? 'customer.email' : `patient[${ patientIndex }].phoneNumber`); } }
            disabled={ (customerIsPatient && !isCustomer) || !userPermissions.update.canUpdatePhone }
          />
        </Col>
      </Row>
    </div>
  );
};

UserDetailsForm.propTypes = propTypes;
UserDetailsForm.defaultProps = defaultProps;

export default UserDetailsForm;
